import React, { useContext } from 'react';
import { timeDay } from 'd3-time';

import BaseSVG, { zeroMargins } from './BaseSVG';
import D3Component from './D3Component';
import DayInfoGraph from './DayInfoGraph';
import GlucoseDayGraph from './GlucoseDayGraph';
import GlucoseSVG from './GlucoseSVG';
import MainView from './MainView';
import PresentationContext from '../PresentationContext';
import { parseDate, urlDateFormat } from '../utils/formats';
import './Dashboard.css';

function DailyView(props) {
  const {
    day,
    days,
    interactive = true,
    basalData,
    bolusData,
    eventData,
    exerciseData,
    longActingData,
    notesData,
    nutritionData,
    photosData,
    sgvData,
    sleepData,
    smbgData,
    stepsData,
    t,
    timeScale,
  } = props;

  const {
    formatDate,
    formatGlucose,
    formatTimeString,
    locale,
    longDateFormat,
  } = useContext(PresentationContext);

  const date = day ? parseDate(day) : new Date();
  const formattedDay = longDateFormat(date);
  const times = [date, timeDay.offset(date, 1)];

  const InfoRowLabels = (
    <g id="InfoRowLabels" />
  );

  const previousDay = urlDateFormat(timeDay.offset(date, -1));

  const previousDayBasal = basalData[previousDay];
  const extendedBasalData = [...(basalData[day] || [])];
  if (previousDayBasal) {
    const lastOfPreviousDay = previousDayBasal[previousDayBasal.length - 1];
    if (lastOfPreviousDay) {
      extendedBasalData.unshift(lastOfPreviousDay);
    }
  }

  const previousDayLongActing = longActingData[previousDay];
  const extendedLongActingData = [...(longActingData[day] || [])];
  if (previousDayLongActing) {
    const lastOfPreviousDay = previousDayLongActing[previousDayLongActing.length - 1]
    if (lastOfPreviousDay) {
      extendedLongActingData.unshift(lastOfPreviousDay);
    }
  }

  const previousNightSleep = sleepData[previousDay];
  const extendedSleepData = [...(sleepData[day] || [])];
  if (previousNightSleep) {
    const lastOfPreviousDay = previousNightSleep[previousNightSleep.length - 1];
    if (lastOfPreviousDay) {
      extendedSleepData.unshift(lastOfPreviousDay);
    }
  }

  const weekDay = date.getDay(); //Sunday-based day of the week
  const formattedDate = formatDate(date);

  return (
    <MainView
      description={t('daily.description', {day: weekDay, date: formattedDate})}
      heading={t('daily.heading', {day: weekDay, date: formattedDate})}
      interactive={interactive}
      key="mainView"
      times={times}
      title={`Sensotrend Day View: ${formattedDay}`}
    >
      <section>
        <GlucoseSVG times={times} key="mainGraph">
          <D3Component
            data={{ sgv: sgvData[day], smbg: smbgData[day] }}
            day={day} 
            days={days}
            formatGlucose={formatGlucose}
            graph={GlucoseDayGraph}
            timeScale={timeScale}
          />
        </GlucoseSVG>
      </section>
      <section>
        <BaseSVG
          key="infoRowsGraphSVG"
          height={144.18}
          showXAxis={false}
          yAxis={InfoRowLabels}
        >
          <D3Component
            key="infoRowsGraph"
            data={{
              basal: extendedBasalData,
              bolus: bolusData[day],
              carbs: nutritionData[day],
              events: eventData[day],
              exercise: exerciseData[day],
              longActing: extendedLongActingData,
              notes: notesData[day],
              photos: photosData[day],
              sleep: extendedSleepData,
              steps: stepsData[day],
            }}
            day={day}
            formatTimeString={formatTimeString}
            graph={DayInfoGraph}
            locale={locale}
            t={t}
            timeScale={timeScale}
          />
        </BaseSVG>
      </section>
      <section>
        <BaseSVG height={0} svgWidth={0} svgHeight={0} margins={zeroMargins} showXAxis={false} key="secondAdditionalGraph" />
      </section>
    </MainView>
  );
};

export default DailyView;
